/**
 * Data hooks for the EHR connection registry, over engage-v2-backend's
 * `checkEhrZone` and `registerEhrConnection`. Both are VITALL only. Errors are
 * returned to the caller rather than sent to the snackbar, because the
 * register dialog shows them inline next to the field they concern. Named apart
 * from the generated `useCheckEhrZone` / `useRegisterEhrConnection`, which route
 * errors to the snackbar.
 */
import { ApolloError } from '@apollo/client';

import {
  useCheckEhrZoneLazyQuery,
  useRegisterEhrConnectionMutation,
} from 'generated/ehr-sync.generated';
import type {
  EhrEnvironment,
  EhrVendor,
  EhrZoneCheckResult,
} from 'generated/types';

/** What checkEhrZone found. */
export interface EhrZoneCheck {
  zoneName: string;
  result: EhrZoneCheckResult;
  zonePrefix: string | null;
  zoneId: string | null;
  channelCount: number | null;
  registeredAccountName: string | null;
}

/** The fields registerEhrConnection takes. */
export interface RegisterEhrConnectionArgs {
  accountId: string;
  zoneName: string;
  vendor: EhrVendor;
  environment: EhrEnvironment;
  fhirBaseUrl: string;
  network?: string;
}

/**
 * Checks a zone name. The query always goes to the network, so a zone created
 * in QIE a moment ago is found.
 * @returns The check function, resolving to the result or the error, and a loading flag
 */
export const useEhrZoneCheck = (): [
  (zoneName: string) => Promise<{ check?: EhrZoneCheck; error?: ApolloError }>,
  boolean,
] => {
  const [query, { loading }] = useCheckEhrZoneLazyQuery({
    fetchPolicy: 'network-only',
  });
  const check = async (
    zoneName: string
  ): Promise<{ check?: EhrZoneCheck; error?: ApolloError }> => {
    let result;
    try {
      result = await query({
        variables: { input: { qie_zone_name: zoneName } },
      });
    } catch (error) {
      return { error: error as ApolloError };
    }
    const { data, error } = result;
    if (error) {
      return { error };
    }
    if (!data) {
      // Treated as a failed request, so the dialog reports QIE as unreachable.
      return {
        error: new ApolloError({
          networkError: new Error('checkEhrZone returned no data'),
        }),
      };
    }
    const c = data.checkEhrZone;
    return {
      check: {
        zoneName: c.qie_zone_name,
        result: c.result,
        zonePrefix: c.zone_prefix ?? null,
        zoneId: c.qie_zone_id ?? null,
        channelCount: c.channel_count ?? null,
        registeredAccountName: c.registered_account_name ?? null,
      },
    };
  };
  return [check, loading];
};

/**
 * Registers a connection, then refreshes the Integrations list.
 * @returns The register function, resolving to an error when it failed, and a loading flag
 */
export const useEhrConnectionRegistration = (): [
  (args: RegisterEhrConnectionArgs) => Promise<{ error?: ApolloError }>,
  boolean,
] => {
  const [mutate, { loading }] = useRegisterEhrConnectionMutation({
    refetchQueries: ['listEhrSyncConnections'],
    awaitRefetchQueries: true,
  });
  const register = async (args: RegisterEhrConnectionArgs) => {
    try {
      // Mutations default to errorPolicy 'all', so a rejected register
      // resolves with `errors` instead of throwing.
      const { errors } = await mutate({
        variables: {
          input: {
            account_id: args.accountId,
            qie_zone_name: args.zoneName.trim(),
            ehr_vendor: args.vendor,
            environment: args.environment,
            fhir_base_url: args.fhirBaseUrl.trim(),
            network: args.network?.trim() || undefined,
          },
        },
      });
      if (errors?.length) {
        return { error: new ApolloError({ graphQLErrors: errors }) };
      }
      return {};
    } catch (error) {
      return { error: error as ApolloError };
    }
  };
  return [register, loading];
};
